import React, { Component } from 'react';
import { PropTypes } from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';
import { ssgFormsAdapters, numberUtils, isNullOrUndefined } from '../common';
import * as intakeActions from './intake.actions';
import * as programActions from '../programs/program.actions';
import { Title, Header } from '../layout';
import NotFound from '../not-found';
import { ConfirmModalWindow, SsgFormComponent, LoadingPane, accessibilityNotificationsEmitter, ssgFormActions } from '@social-solutions/ssg-forms';
import * as programUtils from '../common/program-utilities';
import './intake-form.scss';
import { Map as ImmutableMap } from 'immutable'
import { tenantHelpers, integrationTypes } from '../tenantInfo';

export class IntakeFormComponent extends Component {
  constructor() {
    super();
    this.state = {
      localState: null, // hold the state needed to call save
      localModal: null  // hold a reference to the confirmation modal
    }
  }

  componentDidMount() {
    const { intakeActions, match, programActions } = this.props;
    const { params } = match;
    const { id } = params;

    programActions.fetchAllPrograms(true);
    intakeActions.fetchIntakeForm(id);
    intakeActions.getForm(id)
  }

  componentWillUpdate(nextProps, nextState) {
    const { intakeActions, match : oldMatch, programActions } = this.props;
    const { params : oldParams } = oldMatch;
    const { id : oldId } = oldParams;

    const { match } = nextProps;
    const { params } = match;
    const { id } = params;

    if(id && id !== oldId) {
      programActions.fetchAllPrograms(true);
      intakeActions.fetchIntakeForm(id);
      intakeActions.getForm(id)
    }
  }

  componentWillUnmount() {
    const { ssgFormActions, match } = this.props;
    const { params } = match;
    const { id } = params;

    ssgFormActions.hardReset('intake-' + id);
  }

  /**
   * For the given intake form id (guid), get the default selected program id (stored as programEnrollmentId),
   * convert it to a lowercase guid, and return it in an array.
   * @param {string} intakeFormId Guid of the intake form. No 'intake-' prefix should be supplied here.
   * @returns {array} Set of guids; should contain max 1 entry. Will be empty if no data could be found.
   */
  getDefaultSelectedPrograms(intakeFormId) {
    const { intake } = this.props;
    const { intakeForms } = intake || {};
    const intakeFormState = intakeForms && intakeForms.get(intakeFormId);
    if (!!intakeFormState) {
      const { data : intakeForm } = intakeFormState || {};
      if (!!intakeForm && !!intakeForm.programEnrollmentId) {
        return [numberUtils.convertIntToGuid(intakeForm.programEnrollmentId).toLowerCase()];
      }
    }

    return [];
  }

  /**
   * Prompt the user for confirmation of form data submission, as
   * long as the data has passed validation.
   * @param {*} state State to use if the user confirms submission via the modal.
   */
  handleConfirmPopupWindowDisplay(state) {
    if(!!state && !!state.validationResult && state.validationResult.passedValidation) {
      this.setState({localState : state});
      this.state.localModal.show();
    }
    else {
      window.scrollTo(0, 0);
    }
  }

  /**
   * Save the data, or return to the form data entry, based on the user's choice
   * in the confirm modal.
   * @param {*} choice True if the user picked yes on the modal, false otherwise
   * @param {*} state State to use for saving, if the user selected yes.
   */
  handleConfirmPopupWindowChoice(choice, state) {
    if (choice) {
      this.handleSave(state);
    }
    else {
      this.setState({localState : null});
    }
  }

  handleResetForm() {
    accessibilityNotificationsEmitter.assertive("Cleared responses to the intake form.");
  }

  handleSave(state) {
    const { intakeActions, match, redirectTo } = this.props;
    const { params, url } = match;
    const { id } = params;
    if(!!state && !!state.validationResult && state.validationResult.passedValidation) {
      intakeActions.submitIntakeForm(id, state.value, state.recaptchaValue);
      redirectTo(`${url}/results`);
    }
  }

  /**
   *
   * @param {PreviewFormElements} elements
   * @returns {Map<string, string>} A map of the Intake Field Default values
   */
  _convertFormDefaultsToMap(elements) {
    const defaultMap = new Map();

    elements.forEach(({ defaultValue, label, sourceData, subElements, type, typeLabel }) => {
      // skip the `Description` field in Intake Forms
      if (
        type.toLowerCase() === 'help' ||
        typeLabel.toLowerCase() === 'Instructions'.toLowerCase()
      ) {
        return;
      }

      // skip if label is empty
      if (label === "" || label === undefined) return;

      if (Array.isArray(subElements)) {
        subElements.forEach(({ defaultValue, id }) => {
          defaultMap.set(id, defaultValue);
        });
      } else {
        defaultMap.set(sourceData.id, defaultValue);
      }
    });

    return defaultMap;
  }

  /**
   * Converts the keys in initialValues to match the sourceData.id from formData elements
   * @param {Array} formData - The form data array containing elements with sourceData
   * @param {Map} initialValues - The initial values map to convert
   * @returns {Map} - A new Map with converted keys
   */
  _convertInitialValuesKeys(formData, initialValues) {
    const convertedMap = new Map();
    
    // Create a mapping of sourceData.id to element id
    const sourceDataMap = new Map();
    formData.forEach(element => {
      if (element.sourceData && element.sourceData.id) {
        sourceDataMap.set(element.sourceData.id, element.id);
      }
      
      // Handle nested elements in groups
      if (element.subElements) {
        element.subElements.forEach(subElement => {
          if (subElement.sourceData && subElement.sourceData.id) {
            sourceDataMap.set(subElement.sourceData.id, subElement.id);
          }
        });
      }
    });

    // Convert the initialValues keys
    initialValues.forEach((value, key) => {
      // If the key exists in sourceDataMap, use the mapped value
      // Otherwise keep the original key
      const newKey = sourceDataMap.get(key) || key;
      let foundDefault = undefined;
      const splitKey = newKey.split('-');
      
      // If other is enabled this is to account for setting defaults 
        if (
          !value &&
          !Number.isNaN(Number(splitKey[0])) &&
          splitKey[1] === 'options'
        ) {
          const foundElement = formData.find((form) => {
            return form.sourceData &&
            form.sourceData.id &&
            form.sourceData.id === newKey.split('-')[0]
          });

          foundDefault = foundElement.defaultValue;
        }

      convertedMap.set(newKey, value || foundDefault);
    });
    return convertedMap;
  }

  /**
   *
   * @param {PreviewForm} previewForm
   * @returns {PreviewForm} A mutated preview form to match the shape of TouchpointForm
   */
  _massagePreviewForm(previewForm) {
    return previewForm;
  }

  render() {
    const { intake, ssgForms, match, programs } = this.props;
    const { params } = match;
    const { id } = params;
    const { intakeForms, previewForms, formSubmission } = intake || {};
    const data = previewForms.get(id) && previewForms.get(id).get('data');
    const fieldFormatSetting = !!data && data.fieldFormatSetting;

    const progResult = programUtils.getAllPrograms(programs);

    const intakeFormState = intakeForms && intakeForms.get(id);
    const { data : intakeForm } = intakeFormState || {};
    const { isPublished, disableReCaptcha } = intakeForm || {};

    const previewFormState = previewForms && previewForms.get(id);
    const { data : previewForm } = previewFormState || {};

    const submitAnswersState = formSubmission && formSubmission.get(id);
    const { isFetching : isSubmitting } = submitAnswersState || {};

    const { processedForms } = ssgForms || {};
    const formState = processedForms && processedForms.get('intake-' + id);
    const { value, recaptchaValue } = formState || { value: null, recaptchaValue: null }
    const formData = this._massagePreviewForm(previewForm);
    var hasValues = !!value && !value.isEmpty();
    var recaptchaValid = disableReCaptcha || !!recaptchaValue;
    var invalidProgramAssignmentType = previewForm && previewForm.demographicProgramAssignmentType === 'all' && previewForm.touchpointProgramAssignmentType === 'all';
    const shouldUseInitialValues = tenantHelpers.getIntegrationType() === integrationTypes.apricot;

    const initialValues = !isNullOrUndefined(previewForm) && shouldUseInitialValues
      ? this._convertFormDefaultsToMap(previewForm.elements)
      : new Map();

    const intakeInitialValues = !isNullOrUndefined(formData) && shouldUseInitialValues
      ? this._convertInitialValuesKeys(previewForm.elements, initialValues)
      : new Map();


    if (invalidProgramAssignmentType) {
      console.error("[Error]: Issue with program assignment type");
      console.log(formData);
    }

    const confirmModalProps = {
      confirmTitle: 'Submit Intake',
      confirmMsg: 'Are you ready to submit this intake form?'
    }

     const formProps = {
      id: 'intake-' + id,
      form: { ...formData, includeRecaptcha: !disableReCaptcha },
      saving: isSubmitting,
      recaptchaKey: process.env.RECAPTCHA_PUBLIC_KEY,
      buttonLayout: [
        {
          id: 'reset',
          display: 'Reset',
          ariaLabel: 'Reset Form',
          buttonType: 'secondary',
          functionType: 'reset',
          onClick: (state) => this.handleResetForm(),
        },
        {
          id: 'submit',
          display: 'Submit',
          ariaLabel: 'Submit Form',
          type: 'submit',
          buttonType: 'primary',
          functionType: 'submit',
          spinnerOn: isSubmitting,
          disabled: !hasValues || !recaptchaValid,
          onClick: (state) => this.handleConfirmPopupWindowDisplay(state)
        }
      ],
      showRequiredKey: true,
      allPrograms: progResult,
      initialValues: shouldUseInitialValues ? ImmutableMap(intakeInitialValues) : undefined,
      initialSelectedPrograms: this.getDefaultSelectedPrograms(id),
      css: {
        form: "ssg-form intake-form-display",
        formBody: 'ssg-forms-body',
        actions: "ssg-forms-actions intake-form-actions-display",
      },
      country: fieldFormatSetting !== false ? fieldFormatSetting : undefined
    };

    var intakeLoadingState = ssgFormsAdapters.convertLoadingState(intakeFormState);

    var validElements = [];
    let finalState;
    if (!!intakeLoadingState && !!intakeLoadingState.data && !! intakeLoadingState.data.demographicQuestions) {
      intakeLoadingState.data.demographicQuestions.forEach(q => {
        if (!!previewForm && !!previewForm.elements) {
          previewForm.elements.forEach(e => {
            if ((e.id === q.id && !e.disabled) || e.type === 'help') {
              validElements.push(e.id);
            }
          })
        }
      });

      if (!!intakeLoadingState.data.touchpointQuestions) {
        intakeLoadingState.data.touchpointQuestions.forEach(q => {
          if (!!previewForm && !!previewForm.elements) {
            previewForm.elements.forEach(e => {
              if (e.id === q.id && !e.disabled) {
                validElements.push(e.id);
              }
            })
          }
        })
      }

      if (!!previewForm && !!previewForm.elements) {
        finalState = previewFormState.data.elements.filter(e => validElements.includes(e.id));
        previewFormState.data.elements = finalState;
      }
    }

    var allProgramsLoadingState = ssgFormsAdapters.convertLoadingState(programs && programs.allPrograms);

    return (
      <div>
        <Header />
        <Title title="Intake Form"/>
        <div id="view" className="intake-view">
           <LoadingPane id="publish" loadingState={[intakeLoadingState, allProgramsLoadingState]}>
            { isPublished && !invalidProgramAssignmentType ? (
              <div className="validated-section">
                <div id="intake-form" className="intake-form">
                  <LoadingPane id="display" loadingState={ssgFormsAdapters.convertLoadingState(previewFormState)}>
                    <ConfirmModalWindow id="intake-form-confirm"
                      title={confirmModalProps.confirmTitle}
                      modalRef={(modal) => this.setState( {localModal : modal})}
                      onClose={(e, confirmed) => this.handleConfirmPopupWindowChoice(confirmed, this.state.localState)}>
                      {confirmModalProps.confirmMsg}
                    </ConfirmModalWindow>
                    <SsgFormComponent {...formProps}/>
                  </LoadingPane>
                </div>
            </div>
            ) : invalidProgramAssignmentType ? (
              <div>
                <h1>There was an error loading this page. Please reach out the organization directly.</h1>
              </div>
            )
           : (<NotFound/>)}
          </LoadingPane>
        </div>
      </div>
    );
  }
}

IntakeFormComponent.propTypes = {
  intakeActions: PropTypes.object.isRequired,
  intake: PropTypes.object.isRequired,
  ssgForms: PropTypes.object.isRequired,
  programs: PropTypes.object.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.isRequired,
    })
  })
};

const mapStateToProps = ({ intake, ssgForms, programs }) => {
  return {
    intake,
    ssgForms,
    programs
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    intakeActions: bindActionCreators(intakeActions, dispatch),
    ssgFormActions: bindActionCreators(ssgFormActions, dispatch),
    redirectTo: bindActionCreators(push, dispatch),
    programActions: bindActionCreators(programActions, dispatch)
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(IntakeFormComponent);


/**
 * @typedef {Object} IntakeFormState
 *
 * @property {boolean} isFetching
 * @property {error} err
 * @property {Map<string, IntakeForm>} intakeForms
 * @property {Map<string, IntakeResponses>} intakeResponses
 */

/**
 * @typedef {Object} IntakeForm
 *
 * @property {unknown} data
 * @property {error} err
 * @property {boolean} isFetching
 * @property {boolean} isValid
 */

/**
 * @typedef {Object} IntakeResponses
 *
 * @property {unknown} data
 * @property {error} err
 * @property {boolean} isFetching
 * @property {boolean} isValid
 */

/**
 * @typedef {Object} PreviewForm
 * @property {Date} auditDate
 * @property {number} auditStaffId
 * @property {string} demographicProgramAssignmentType
 * @property {boolean} disabled
 * @property {PreviewFormElements} elements
 * @property {"US" | "CA" | "UK"} fieldFormatSetting
 * @property {Object} formDefaults
 * @property {string | number} id
 * @property {boolean} includeRecaptcha
 * @property {boolean} isUnsupported
 * @property {idk} maxTotalAttachmentSize
 * @property {string} name
 * @property {string} programAssignmentType
 * @property {boolean} questionNumberingEnabled
 * @property {Array<idk>} rules
 * @property {GUID} tenantId
 * @property {string} timeZoneSetting
 * @property {string} touchpointProgramAssignmentType
 */

/**
 * @typedef {Object} PreviewFormElements
 *
 * @property {string} ariaLabel
 * @property {string} autoComplete
 * @property {string} defaultValue
 * @property {boolean} disabled
 * @property {boolean} hidden
 * @property {string} id
 * @property {boolean} isPreviewElement
 * @property {boolean} isRequired
 * @property {boolean} isUnsupported
 * @property {string} label
 * @property {string} questionType
 * @property {boolean} readOnly
 * @property {SourceData?} sourceData
 * @property {boolean} storesData
 * @property {Array<PreviewFormElements>} subElements
 * @property {string} tooltip
 * @property {string} type
 * @property {string} typeLabel
 */

/**
 * @typedef {Object} SourceData
 *
 * @property {string} id
 * @property {string} type
 */



// WEBPACK FOOTER //
// src/intake/intake-form.component.js